import React, { useState } from 'react';
import { motion, AnimatePresence, cubicBezier } from 'framer-motion';

interface NewsletterOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewsletterOverlay({ isOpen, onClose }: NewsletterOverlayProps) {
  const easing = cubicBezier(0.77, 0, 0.175, 1); // custom cubic-bezier easing
  // const easing: [number, number, number, number] = [0.77, 0, 0.175, 1];
  const [submitted, setSubmitted] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    setStatusMessage(null);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            email_address: email,
            form_type: 'Newsletter',
          },
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setEmail('');
      } else {
        console.error(result);
        setStatusMessage({ type: 'error', text: 'Something went wrong. Please try again later.' });
      }
    } catch (error) {
      console.error(error);
      setStatusMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: easing }}
            className="fixed inset-0 bg-black"
            onClick={onClose}
          ></motion.div>

          {/* Slide-in panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.5, ease: easing }}
            className="ml-auto w-full max-w-md h-full bg-[#B6C380] shadow-lg p-5 flex flex-col justify-between z-10"
          >
            <button
              onClick={onClose}
              className="self-end mb-4 font-light text-[#2B332A] hover:text-[#9E703E]"
              aria-label="Close newsletter"
            >
              Close
            </button>

            <h2 className="text-3xl font-light mb-4 flex flex-col w-full text-center text-[#2B332A]">
              <small className="text-base font-light">Join the</small> Newsletter
            </h2>

            <div>
              {submitted ? (
                <p className="text-center text-[#2B332A]">Thank you for subscribing!</p>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="mb-4 px-4 py-3 border-b border-[#2B332A] bg-transparent focus:outline-none placeholder-[#2B332A66] w-full text-center"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#2B332A] text-[#B6C380] py-3 hover:bg-[#2B332A] hover:text-[#B6C380] transition text-[15px] font-light"
                  >
                    {loading ? 'Submitting...' : 'Subscribe'}
                  </button>
                </form>
              )}
              {statusMessage && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 text-sm font-medium ${
                    statusMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {statusMessage.text}
                </motion.p>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
